import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, getDocs, updateDoc, doc, getDoc, arrayRemove, arrayUnion, Timestamp, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';

interface Match {
  id: string;
  tournamentId: string;
  round: number;
  matchNumber: number;
  player1Id: string;
  player1Name: string;
  player2Id: string;
  player2Name: string;
  winnerId: string | null;
  score1: number;
  score2: number;
  status: 'pending' | 'ongoing' | 'completed';
  scheduledTime: Date | null;
}

interface Participant {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  tournamentId: string;
  registeredAt: Date;
  status: 'registered' | 'checked_in' | 'withdrawn';
}

interface Tournament {
  id: string;
  name: string;
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
  participantIds: string[];
  bannedUsers: string[];
  maxParticipants: number;
  bracketType: 'singleElimination' | 'doubleElimination';
  firstPlace?: string;
  secondPlace?: string;
  thirdPlace?: string;
  createdBy: string;
}

interface BracketManagementProps {
  tournamentId: string;
  tournamentName: string;
  tournamentType: 'single_elimination' | 'double_elimination' | 'round_robin' | 'swiss';
}

const BracketManagement: React.FC<BracketManagementProps> = ({ tournamentId, tournamentName, tournamentType }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [matches, setMatches] = useState<Match[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [showParticipants, setShowParticipants] = useState(false);
  const [kickingParticipant, setKickingParticipant] = useState<string | null>(null);

  useEffect(() => {
    if (tournamentId) {
      loadTournament();
      loadMatches();
      loadParticipants();
    }
  }, [tournamentId]);

  const loadTournament = async () => {
    try {
      const tournamentDoc = await getDoc(doc(db, 'tournaments', tournamentId));
      if (tournamentDoc.exists()) {
        setTournament({ id: tournamentDoc.id, ...tournamentDoc.data() } as Tournament);
      }
    } catch (error) {
      console.error('Error loading tournament:', error);
    }
  };

  const loadMatches = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'tournament_matches'),
        where('tournamentId', '==', tournamentId)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        scheduledTime: doc.data().scheduledTime?.toDate() || null
      })) as Match[];

      setMatches(data.sort((a, b) => a.round - b.round || a.matchNumber - b.matchNumber));
    } catch (error) {
      console.error('Error loading matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadParticipants = async () => {
    try {
      const tournamentDoc = await getDoc(doc(db, 'tournaments', tournamentId));
      if (!tournamentDoc.exists()) return;

      const participantIds = tournamentDoc.data().participantIds || [];
      const participantsData: Participant[] = [];

      for (const userId of participantIds) {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          participantsData.push({
            id: userId,
            userId: userId,
            userName: userData.displayName || userData.email || 'Unknown',
            userEmail: userData.email || '',
            tournamentId: tournamentId,
            registeredAt: new Date(),
            status: 'registered'
          });
        }
      }

      setParticipants(participantsData);
    } catch (error) {
      console.error('Error loading participants:', error);
    }
  };

  const updateMatchResult = async (matchId: string, winnerId: string, score1: number, score2: number) => {
    try {
      const match = matches.find(m => m.id === matchId);
      if (!match) return;

      await updateDoc(doc(db, 'tournament_matches', matchId), {
        winnerId,
        score: {
          [match.player1Id]: score1,
          [match.player2Id]: score2
        },
        status: 'completed',
        completedAt: Timestamp.now()
      });

      const allMatches = await getDocs(query(
        collection(db, 'tournament_matches'),
        where('tournamentId', '==', tournamentId)
      ));
      const allCompleted = allMatches.docs.every(doc => doc.data().status === 'completed');

      if (allCompleted && tournament) {
        const finalMatch = matches.find(m => m.round === Math.max(...matches.map(m => m.round)));
        if (finalMatch && finalMatch.winnerId) {
          await updateDoc(doc(db, 'tournaments', tournamentId), {
            status: 'completed',
            firstPlace: finalMatch.winnerId,
            completedAt: Timestamp.now()
          });
        }
      }

      loadMatches();
      loadTournament();
      setSelectedMatch(null);
    } catch (error) {
      console.error('Error updating match:', error);
    }
  };

  const handleKickParticipant = async (participantId: string) => {
    if (!tournament) return;

    if (tournament.status !== 'upcoming') {
      alert(i18n.language === 'hu'
        ? 'Csak közelgő versenynél lehet résztvevőt törölni!'
        : 'Can only remove participants from upcoming tournaments!');
      return;
    }

    if (!window.confirm(i18n.language === 'hu'
      ? 'Biztosan törölni szeretnéd ezt a résztvevőt a versenyből?'
      : 'Are you sure you want to remove this participant from the tournament?')) {
      return;
    }

    try {
      setKickingParticipant(participantId);

      await updateDoc(doc(db, 'tournaments', tournamentId), {
        participantIds: arrayRemove(participantId),
        bannedUsers: arrayUnion(participantId)
      });

      const matchesToUpdate = await getDocs(query(
        collection(db, 'tournament_matches'),
        where('tournamentId', '==', tournamentId)
      ));

      for (const matchDoc of matchesToUpdate.docs) {
        const matchData = matchDoc.data();
        if (matchData.player1Id === participantId) {
          await updateDoc(matchDoc.ref, {
            player1Id: null,
            player1Name: null
          });
        }
        if (matchData.player2Id === participantId) {
          await updateDoc(matchDoc.ref, {
            player2Id: null,
            player2Name: null
          });
        }
      }

      alert(i18n.language === 'hu'
        ? 'Résztvevő sikeresen törölve!'
        : 'Participant removed successfully!');

      loadTournament();
      loadParticipants();
      loadMatches();
    } catch (error) {
      console.error('Error kicking participant:', error);
      alert(i18n.language === 'hu'
        ? 'Hiba történt a résztvevő törlése során!'
        : 'Error removing participant!');
    } finally {
      setKickingParticipant(null);
    }
  };

  const getRoundName = (round: number, totalRounds: number) => {
    if (round === totalRounds) return t('tournament.final');
    if (round === totalRounds - 1) return t('tournament.semiFinal');
    if (round === totalRounds - 2) return t('tournament.quarterFinal');
    return `${t('tournament.round')} ${round}`;
  };

  const getMatchStatusColor = (status: Match['status']) => {
    switch (status) {
      case 'pending': return 'bg-gray-500/20 text-gray-400';
      case 'ongoing': return 'bg-green-500/20 text-green-400';
      case 'completed': return 'bg-blue-500/20 text-blue-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const groupedMatches = matches.reduce((acc, match) => {
    if (!acc[match.round]) {
      acc[match.round] = [];
    }
    acc[match.round].push(match);
    return acc;
  }, {} as Record<number, Match[]>);

  const totalRounds = Math.max(...matches.map(m => m.round), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-[#B8B8C3]">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card-dark p-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-2xl font-bold text-white mb-2">{tournamentName}</h3>
            <div className="flex items-center gap-3">
              <p className="text-[#B8B8C3]">{t(`tournament.type.${tournamentType}`)}</p>
              {tournament && (
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${tournament.status === 'upcoming' ? 'bg-blue-500/20 text-blue-400' :
                  tournament.status === 'ongoing' ? 'bg-green-500/20 text-green-400' :
                    tournament.status === 'completed' ? 'bg-purple-500/20 text-purple-400' :
                      'bg-red-500/20 text-red-400'
                  }`}>
                  {t(`tournament.status.${tournament.status}`)}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={() => setShowParticipants(!showParticipants)}
            className="btn-secondary flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            {i18n.language === 'hu' ? 'Résztvevők' : 'Participants'} ({participants.length})
          </button>
        </div>
      </div>

      {/* Participants List */}
      {showParticipants && (
        <div className="card-dark p-6">
          <h4 className="text-xl font-bold text-white mb-4">📋 {i18n.language === 'hu' ? 'Résztvevők listája' : 'Participants List'}</h4>
          {participants.length === 0 ? (
            <p className="text-[#B8B8C3] text-center py-8">
              {i18n.language === 'hu' ? 'Még nincs regisztrált résztvevő' : 'No registered participants yet'}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {participants.map((participant, index) => (
                <div
                  key={participant.id}
                  className="bg-[#15191E] p-4 rounded-lg flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-semibold">{participant.userName}</p>
                    <p className="text-xs text-[#B8B8C3]">{participant.userEmail}</p>
                  </div>
                  {tournament?.status === 'upcoming' && (
                    <button
                      onClick={() => handleKickParticipant(participant.userId)}
                      disabled={kickingParticipant === participant.userId}
                      className="px-3 py-1 bg-red-500/10 text-red-400 rounded hover:bg-red-500/20 transition-colors text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      title={i18n.language === 'hu' ? 'Résztvevő eltávolítása' : 'Remove participant'}
                    >
                      {kickingParticipant === participant.userId ? '...' : '✕'}
                    </button>
                  )}
                  {tournament?.firstPlace === participant.userId && (
                    <span className="px-2 py-1 rounded text-xs font-semibold bg-yellow-500/20 text-yellow-400">
                      🥇 {i18n.language === 'hu' ? '1. hely' : '1st Place'}
                    </span>
                  )}
                  {tournament?.secondPlace === participant.userId && (
                    <span className="px-2 py-1 rounded text-xs font-semibold bg-gray-400/20 text-gray-300">
                      🥈 {i18n.language === 'hu' ? '2. hely' : '2nd Place'}
                    </span>
                  )}
                  {tournament?.thirdPlace === participant.userId && (
                    <span className="px-2 py-1 rounded text-xs font-semibold bg-orange-500/20 text-orange-400">
                      🥉 {i18n.language === 'hu' ? '3. hely' : '3rd Place'}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {matches.length === 0 ? (
        <div className="card-dark p-12 text-center">
          <div className="text-6xl mb-4">🎯</div>
          <h3 className="text-xl font-bold text-white mb-2">{t('tournament.noBracket')}</h3>
          <p className="text-[#B8B8C3]">{t('tournament.noBracketDesc')}</p>
        </div>
      ) : (
        <div className="card-dark p-6">
          <h3 className="text-xl font-bold text-white mb-6">
            {i18n.language === 'hu' ? 'Verseny Bracket' : 'Tournament Bracket'}
          </h3>

          {/* Tournament Bracket Tree */}
          <div className="overflow-x-auto pb-4">
            <div className="flex min-w-max px-8 py-4" style={{ gap: '80px' }}>
              {Object.keys(groupedMatches).sort((a, b) => Number(a) - Number(b)).map((roundKey, roundIndex) => {
                const round = Number(roundKey);
                const roundMatches = groupedMatches[round];
                const isLastRound = roundIndex === Object.keys(groupedMatches).length - 1;
                const matchHeight = 100;
                const baseGap = 20;
                const matchGap = baseGap * Math.pow(2, roundIndex);
                const topMargin = roundIndex > 0 ? (baseGap * Math.pow(2, roundIndex - 1)) + (matchHeight / 2) : 0;

                return (
                  <div key={round} className="flex flex-col" style={{ minWidth: '280px' }}>
                    {/* Round Header */}
                    <div className="text-center mb-6">
                      <h4 className="text-lg font-bold text-cyan-400">
                        {getRoundName(round, totalRounds)}
                      </h4>
                    </div>

                    {/* Matches in this round */}
                    <div className="flex flex-col relative" style={{
                      gap: `${matchGap}px`,
                      marginTop: `${topMargin}px`
                    }}>
                      {roundMatches.map((match, matchIndex) => {
                        const isEvenMatch = matchIndex % 2 === 0;
                        const hasNextMatch = matchIndex + 1 < roundMatches.length;

                        return (
                          <div key={match.id} className="relative" style={{ height: `${matchHeight}px` }}>
                            {/* Match Card */}
                            <div
                              className="bg-[#15191E] rounded-lg border border-[#31383D] hover:border-cyan-500/50 transition-all cursor-pointer h-full relative z-10"
                              onClick={() => setSelectedMatch(match)}
                            >
                              {/* Match Header */}
                              <div className="flex justify-between items-center px-3 py-1.5 border-b border-[#31383D] bg-[#151b26] rounded-t-lg">
                                <span className="text-xs text-[#B8B8C3]">
                                  {t('tournament.match')} {match.matchNumber}
                                </span>
                                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getMatchStatusColor(match.status)}`}>
                                  {match.status === 'completed' ? '✓' : match.status === 'ongoing' ? '⚡' : '○'}
                                </span>
                              </div>

                              {/* Players */}
                              <div className="p-2 space-y-1">
                                {/* Player 1 */}
                                <div className={`flex justify-between items-center px-2 py-1.5 rounded ${match.winnerId === match.player1Id
                                    ? 'bg-green-500/20 border border-green-500/40'
                                    : 'bg-[#0f1419] border border-transparent'
                                  }`}>
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    {match.winnerId === match.player1Id && (
                                      <span className="text-green-400 text-xs">👑</span>
                                    )}
                                    <span className={`text-xs font-medium truncate ${match.player1Name ? 'text-white' : 'text-gray-500'
                                      }`}>
                                      {match.player1Name || t('tournament.tbd')}
                                    </span>
                                  </div>
                                  {match.player1Name && match.status === 'completed' && (
                                    <span className={`text-sm font-bold ml-2 ${match.winnerId === match.player1Id ? 'text-green-400' : 'text-gray-400'
                                      }`}>
                                      {match.score1}
                                    </span>
                                  )}
                                </div>

                                {/* Player 2 */}
                                <div className={`flex justify-between items-center px-2 py-1.5 rounded ${match.winnerId === match.player2Id
                                    ? 'bg-green-500/20 border border-green-500/40'
                                    : 'bg-[#0f1419] border border-transparent'
                                  }`}>
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    {match.winnerId === match.player2Id && (
                                      <span className="text-green-400 text-xs">👑</span>
                                    )}
                                    <span className={`text-xs font-medium truncate ${match.player2Name ? 'text-white' : 'text-gray-500'
                                      }`}>
                                      {match.player2Name || t('tournament.tbd')}
                                    </span>
                                  </div>
                                  {match.player2Name && match.status === 'completed' && (
                                    <span className={`text-sm font-bold ml-2 ${match.winnerId === match.player2Id ? 'text-green-400' : 'text-gray-400'
                                      }`}>
                                      {match.score2}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Connector Lines */}
                            {!isLastRound && (
                              <>
                                {/* Horizontal line from match center to right */}
                                <div
                                  className="absolute bg-cyan-500/50"
                                  style={{
                                    top: '50%',
                                    left: '100%',
                                    width: '40px',
                                    height: '2px',
                                    transform: 'translateY(-50%)'
                                  }}
                                />

                                {/* Vertical line connecting pairs */}
                                {isEvenMatch && hasNextMatch && (
                                  <>
                                    <div
                                      className="absolute bg-cyan-500/50"
                                      style={{
                                        top: '50%',
                                        left: 'calc(100% + 40px)',
                                        width: '2px',
                                        height: `${matchHeight + matchGap}px`
                                      }}
                                    />
                                    {/* Horizontal line from vertical to next round */}
                                    <div
                                      className="absolute bg-cyan-500/50"
                                      style={{
                                        top: `calc(50% + ${(matchHeight + matchGap) / 2}px)`,
                                        left: 'calc(100% + 40px)',
                                        width: '40px',
                                        height: '2px'
                                      }}
                                    />
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Match Result Modal */}
      {selectedMatch && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-dark p-6 max-w-md w-full">
            <h3 className="text-2xl font-bold text-white mb-6">
              {t('tournament.matchResult')}
            </h3>

            <div className="space-y-4">
              <div className="text-center mb-4">
                <p className="text-sm text-[#B8B8C3]">
                  {getRoundName(selectedMatch.round, totalRounds)} - {t('tournament.match')} {selectedMatch.matchNumber}
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-[#15191E] rounded-lg">
                  <span className="text-white font-semibold">{selectedMatch.player1Name}</span>
                  <input
                    type="number"
                    defaultValue={selectedMatch.score1}
                    className="input-dark w-20 text-center"
                    min="0"
                    id="score1"
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-[#15191E] rounded-lg">
                  <span className="text-white font-semibold">{selectedMatch.player2Name}</span>
                  <input
                    type="number"
                    defaultValue={selectedMatch.score2}
                    className="input-dark w-20 text-center"
                    min="0"
                    id="score2"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    const score1 = Number((document.getElementById('score1') as HTMLInputElement).value);
                    const score2 = Number((document.getElementById('score2') as HTMLInputElement).value);
                    const winnerId = score1 > score2 ? selectedMatch.player1Id : selectedMatch.player2Id;
                    updateMatchResult(selectedMatch.id, winnerId, score1, score2);
                  }}
                  className="btn-primary flex-1"
                >
                  {t('tournament.saveResult')}
                </button>
                <button
                  onClick={() => setSelectedMatch(null)}
                  className="btn-secondary flex-1"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BracketManagement;
